import { useId } from 'react';
import { useDispatch } from 'react-redux';
import { PrimaryButton } from 'ui/button';
import { Body2, fontWeights } from 'ui/theme';
import { ApiError } from 'services/api/instance';
import { usePostRestoreTemplateContent } from 'api/TemplateHistoryAPI';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer/utils';
import { DateFormat, TimeFormat, localizeDateTime } from 'utils/l10n';
import { ModalFooter, ModalBody, ModalHeader, Modal, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { useTemplateHistoryContext } from '../../context/TemplateHistoryContext';
import type { RestoreTemplateModalProps } from '../../types';
import styled from 'styled-components';

const StyledTemplateDate = styled.span`
  font-weight: ${fontWeights.medium};
  text-decoration: underline;
`;

export const RestoreTemplateModal = ({
  templateId,
  newContent,
  onClose,
}: RestoreTemplateModalProps) => {
  const id = useId();
  const dispatch = useDispatch();

  const { refetch } = useTemplateHistoryContext();

  const { mutate } = usePostRestoreTemplateContent({
    onSuccess: () => {
      dispatch(addPositiveToast(`Successfully restored the template content`));
      refetch(); // need to refetch the template history details to reflect that the update has occured
      onClose();
    },
    onError: (error) => {
      const errorText =
        (error as ApiError)?.response?.data?.message || 'Failed to restore template';
      dispatch(addNegativeToast(errorText));
    },
  });

  const onSubmit = () => {
    mutate({
      templateId,
      contentId: newContent.id,
    });

    onClose();
  };

  const date = new Date(newContent.createdAt);
  const dateToDisplay: string = localizeDateTime(date, {
    dateStyle: DateFormat.Short,
    showTime: true,
    timeStyle: TimeFormat.Locale,
  });

  return (
    <Modal
      data-test="template-history-restore-content-modal"
      id={id}
      isModalOpen
      onClose={onClose}
      size={MODAL_SIZES.SMALL}
    >
      <ModalHeader variant={MODAL_VARIANTS.WARNING}>Restore version?</ModalHeader>
      <ModalBody>
        <Body2>
          Are you sure you want to restore template content from{' '}
          <StyledTemplateDate>{dateToDisplay}</StyledTemplateDate>?
        </Body2>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton onClick={onSubmit}>Restore content</PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
